import { useCallback, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import wait from '@helpers/wait';
import { useResponsive } from '@hooks/useResponsive';
import { useAssetMVPermissions } from '@hooks';
import { IS_UXP_WEBVIEW } from '@config/constants';

const event = new MouseEvent('click', {
  bubbles: true,
  cancelable: true,
  view: window
});

export default function useMediaViewerAnnouncementTour({
  isAssetReady,
  isPreparing,
  isUnsupported,
  setSelectedCollapse
}: {
  isAssetReady: boolean;
  isPreparing: boolean;
  isUnsupported: boolean;
  setSelectedCollapse?: (collapse: string | null) => void;
}) {
  const { isLgWidth } = useResponsive();
  const { onStartTour, onCompleteStep } = useOnboardingProcess();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const startTour = searchParams.get('startTour');
  const isEmbeddedWebView = IS_UXP_WEBVIEW;
  const canLeaveComment = useAssetMVPermissions('comment');
  const isShareReviewLink = location.pathname.includes('external-reviewer');

  const onOpenDropdownMenu = useCallback(async (query: string) => {
    const commentHtml = document.querySelector(query);
    commentHtml?.dispatchEvent(event);
    await wait(800);
  }, []);

  const onStartTourAnnouncement = useCallback(() => {
    if (!isLgWidth) {
      onStartTour({
        type: 'announcement-media-viewer-mobile',
        onClickNext: (currentStep) => {
          if (currentStep === 2 && isShareReviewLink) {
            onCompleteStep({
              type: 'announcement-media-viewer-mobile',
              hideCongrats: true
            });
            onStartTour({ type: undefined });
            searchParams.delete('startTour');
            setSearchParams(searchParams, { replace: true });
          }
        }
      });
      return;
    }

    onStartTour({
      type: 'announcement-media-viewer',
      onClickNext: async (currentStep, isExitTour) => {
        if (isExitTour) return;
        if (currentStep === 1 && isShareReviewLink) {
          await onOpenDropdownMenu(
            '.j-tour-announcement-media-viewer-versions-btn'
          );
          return 3;
        }
        if (currentStep === 1) {
          setSelectedCollapse?.('comments');
          await wait(1000);
          await onOpenDropdownMenu(
            '.j-tour-announcement-media-viewer-panel-section-head-btn'
          );
          return;
        }
        if (currentStep === 2) {
          await onOpenDropdownMenu(
            '.j-tour-announcement-media-viewer-versions-btn'
          );
        }
      },
      onClickPrev: async (currentStep, isExitTour) => {
        if (isExitTour) return;
        if (currentStep === 3 && isShareReviewLink) {
          return 1;
        }
        if (currentStep === 3) {
          await wait(1000);
          await onOpenDropdownMenu(
            '.j-tour-announcement-media-viewer-panel-section-head-btn'
          );
        }
      }
    });
  }, [
    onStartTour,
    onCompleteStep,
    isLgWidth,
    isShareReviewLink,
    onOpenDropdownMenu,
    searchParams,
    setSearchParams
  ]);

  useEffect(() => {
    if (isEmbeddedWebView) return;
    if (
      ![
        'announcement-media-viewer',
        'announcement-media-viewer-mobile'
      ].includes(startTour || '')
    )
      return;
    if (!canLeaveComment) return;
    if (isUnsupported) return;
    if (isPreparing) return;
    if (!isAssetReady) return;
    onStartTourAnnouncement();
  }, [
    isEmbeddedWebView,
    startTour,
    canLeaveComment,
    isAssetReady,
    isPreparing,
    isUnsupported,
    onStartTourAnnouncement
  ]);

  return null;
}
